import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Card, Dialog, DialogContent, DialogTitle, Grid, IconButton, MenuItem } from "@mui/material";
import { Close } from "@mui/icons-material";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-material-ui";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { createCaterer, updateCaterer } from "../../../API/caterer";
import { COUNTRIES, REGIONS } from "../../Utils/constants";

const ManageCatererModal = (props) => {
  const { open, data, handleClose } = props;
  const formRef = useRef(null);
  const [caterer, setCaterer] = useState(null);

  useEffect(() => {
    if (open) setCaterer(data);
  }, [data, open]);

  return (
    <Dialog open={open} scroll="paper" fullWidth maxWidth="md"
            sx={{ '& .MuiDialog-paper': { maxHeight: '80vh' } }}>
      <DialogTitle sx={{ backgroundColor: '#1565c0', color: 'white' }}>
        {caterer ? "Edit" : "Add"} Caterer
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => handleClose(false)}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{ padding: '14px', backgroundColor: '#F7F7F7' }}>
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={{
            company_name: caterer?.company_name || "",
            contact_person_name: caterer?.contact_person_name || "",
            country: caterer?.country || "",
            delivery_address: caterer?.delivery_address || "",
            region: caterer?.region || "",
            email: caterer?.email || "",
            phone: caterer?.phone || "",
          }}
          validate={(values) => {
            const errors = {};
          
            if (!values.country) errors.country = "Required";
          
            // ✅ company validation
            if (!values.company_name) {
              errors.company_name = "Required";
            } else if (values.company_name.length < 2) {
              errors.company_name = "company must be at least 2 characters";
            } else if (values.company_name.length > 100) {
              errors.company_name = "company name cannot exceed 100 characters";
            }

            if (!values.contact_person_name) {
              errors.contact_person_name = "Required";
            } 
          
            if (!values.region) errors.region = "Required";
          
            // ✅ Delivery Address validation
            if (values.delivery_address) {
              if (values.delivery_address.length < 5) {
                errors.delivery_address = "Address must be at least 5 characters";
              } else if (values.delivery_address.length > 255) {
                errors.delivery_address = "Address cannot exceed 255 characters";
              }
            }
          
            // ✅ Email validation
            if (!values.email) {
              errors.email = "Required";
            } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
              errors.email = "Invalid email address";
            }
          
            if (!values.phone) {
              errors.phone = "Required";
            } else if (values.country === "Denmark" && !/^\+45\d{8}$/.test(values.phone)) {
              errors.phone = "Phone must be in format +45 followed by 8 digits (e.g., +4512345678)";
            } else if (values.country === "Sweden" && !/^\+46\d{7,10}$|^0\d{7,9}$/.test(values.phone)) {
              errors.phone = "Phone must be in format +46 followed by 7-10 digits or 0 followed by 7-9 digits (e.g., +46701234567 or 0701234567)";
            }
          
            return errors;
          }}          
          onSubmit={(values, { setSubmitting }) => {
            const action = caterer
              ? updateCaterer(caterer.caterer_id, values)
              : createCaterer(values);

            action
              .then((res) => {
                toast.success(res.message || "Caterer saved successfully");
                handleClose(true);
                setSubmitting(false);
              })
              .catch((err) => {
                toast.error(err.message || "Something went wrong");
                setSubmitting(false);
              });
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <Card sx={{ padding: '20px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="company_name"
                      label="Company Name*"
                      component={TextField}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="contact_person_name"
                      label="Contact Person*"
                      component={TextField}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="country"
                      label="Country*"
                      select
                      component={TextField}
                      fullWidth
                      size="small"
                      onChange={(e) => {
                        setFieldValue("country", e.target.value);
                        setFieldValue("region", ""); // Reset region when country changes
                      }}
                    >
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="region"
                      label="Region / City*"
                      select
                      component={TextField}
                      fullWidth
                      size="small"
                      disabled={!values.country}
                    >
                      {values.country && REGIONS[values.country]?.map((r) => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="delivery_address"
                      label="Delivery Address"
                      component={TextField}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="email"
                      label="Email*"
                      component={TextField}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="phone"
                      label="Phone*"
                      component={TextField}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {caterer ? "Update" : "Save"}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ManageCatererModal;